import { ApiResponse } from '@/types';
import { coreApiClient } from './core';
import type {
  TopicsResponse,
  TopicResponse,
  ProgressResponse,
  DialogueStartResponse,
  DialogueResponse,
  QuizStartResponse,
  HistoryResponse,
  HistoryParams,
  ScenarioLevel,
  SkillArea,
  SkillsMapResponse,
} from '@/products/greekbrain/features/AiTutor/api/types';

const BASE_PATH = '/api/v2/ai-tutor';

export const aiTutorApi = {
  // ============ Topics & Progress ============

  async getTopics(): Promise<ApiResponse<TopicsResponse>> {
    try {
      const response = await coreApiClient.axios.get<ApiResponse<TopicsResponse>>(
        `${BASE_PATH}/topics`
      );
      return response.data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to load topics',
      };
    }
  },

  async getTopic(topicKey: string): Promise<ApiResponse<TopicResponse>> {
    try {
      const response = await coreApiClient.axios.get<ApiResponse<TopicResponse>>(
        `${BASE_PATH}/topics/${topicKey}`
      );
      return response.data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to load topic',
      };
    }
  },

  async getProgress(): Promise<ApiResponse<ProgressResponse>> {
    try {
      const response = await coreApiClient.axios.get<ApiResponse<ProgressResponse>>(
        `${BASE_PATH}/progress`
      );
      return response.data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to load progress',
      };
    }
  },

  // ============ Speaking Dialogue ============

  async startDialogue(
    topicKey: string,
    scenarioLevel?: ScenarioLevel,
    modelKey?: string
  ): Promise<ApiResponse<DialogueStartResponse>> {
    try {
      const response = await coreApiClient.axios.post<ApiResponse<DialogueStartResponse>>(
        `${BASE_PATH}/dialogue/start`,
        {
          topic_key: topicKey,
          scenario_level: scenarioLevel,
          model_key: modelKey,
        }
      );
      return response.data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to start dialogue',
      };
    }
  },

  async getDialogue(dialogueId: number): Promise<ApiResponse<DialogueResponse>> {
    try {
      const response = await coreApiClient.axios.get<ApiResponse<DialogueResponse>>(
        `${BASE_PATH}/dialogue/${dialogueId}`
      );
      return response.data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to load dialogue',
      };
    }
  },

  // ============ Quiz Mode ============

  async getSkillsMap(): Promise<ApiResponse<SkillsMapResponse>> {
    try {
      const response = await coreApiClient.axios.get<ApiResponse<SkillsMapResponse>>(
        `/api/greekbrain/skills`
      );
      return response.data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to load skills',
      };
    }
  },

  async startQuiz(options?: {
    modelKey?: string;
    focusSkillArea?: SkillArea;
    focusSkillValue?: string;
    /** Curriculum chapter id (`chapter_1`…): a theme check, every question from that chapter. */
    focusChapter?: string;
  }): Promise<ApiResponse<QuizStartResponse>> {
    try {
      const body: Record<string, string> = {};
      if (options?.modelKey) body.model_key = options.modelKey;
      if (options?.focusSkillArea) body.focus_skill_area = options.focusSkillArea;
      if (options?.focusSkillValue) body.focus_skill_value = options.focusSkillValue;
      if (options?.focusChapter) body.focus_chapter = options.focusChapter;

      const response = await coreApiClient.axios.post<ApiResponse<QuizStartResponse>>(
        `${BASE_PATH}/quiz/start`,
        Object.keys(body).length > 0 ? body : {}
      );
      return response.data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to start quiz',
      };
    }
  },

  async getQuiz(quizId: number): Promise<ApiResponse<DialogueResponse>> {
    try {
      const response = await coreApiClient.axios.get<ApiResponse<DialogueResponse>>(
        `${BASE_PATH}/quiz/${quizId}`
      );
      return response.data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to load quiz',
      };
    }
  },

  // ============ History ============

  async getHistory(params: HistoryParams = {}): Promise<ApiResponse<HistoryResponse>> {
    try {
      const response = await coreApiClient.axios.get<ApiResponse<HistoryResponse>>(
        `${BASE_PATH}/history`,
        {
          params: {
            topic: params.topic,
            type: params.type,
            status: params.status,
            page: params.page || 1,
            per_page: params.per_page || 20,
          },
        }
      );
      return response.data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to load history',
      };
    }
  },
};
